<template>
  <div class="borrowerAttributePercent">
    <BorrowerAttributeConstructor
      :questionText="questionText"
      :description="description"
      :descriptionTooltip="descriptionTooltip"
      :label="null"
      :helpText="helpText"
      :disclaimer="disclaimer"
      >
      <template v-slot:input>
        <div class="form-floating">
          <input
            ref="percent-input"
            :id="question.labelId || 'ba' + identifier"
            :name="question.labelId || 'ba' + identifier"
            class="form-control"
            inputmode="numeric"
            :class="{
              'is-invalid': v$.value && v$.value.$invalid && v$.value.$dirty || validationError,
              'disabled': disabled,
              'disabled-repressed': disabled && repressDisabledStyle,
            }"
            :disabled="disabled || question.group === 'bankValues'"
            :maxlength="4"
            :minlength="question.minLength"
            :placeholder="question.placeholder || label"
            :aria-label="label"
            :required="question.required"
            :value="mask"
            v-maska:[options]
            data-maska="###"
            @focus="onFocus($event)"
            @blur="onBlur(v$.value)"
            @input="setValue($event)"
            @keydown.enter="setValue($event, true)"
            :data-cy="question.alias"
            :dreams-id="question.alias"
          />
          <label class="type-400" :for="question.labelId || 'ba' + identifier"><span>{{ label }}</span></label>
          <client-only><LendioBootstrapTooltip v-if="tooltipText">
              <span class="text-tooltip-icon position-absolute text-primary" data-bs-toggle="tooltip" data-bs-placement="right" :title="tooltipText">
                <fa-icon :icon="['fal', 'circle-info']"></fa-icon>
              </span>
          </LendioBootstrapTooltip></client-only>
          <small class="invalid-feedback" v-if="v$.value && v$.value.$error || validationError">
            <fa-icon :icon="['fal', 'circle-exclamation']"></fa-icon> {{ errorText }}
          </small>
        </div>
      </template>
    </BorrowerAttributeConstructor>
  </div>
</template>

<script>
import { number, percent, unformattedNumber } from '~/libs/format'
import { useVuelidate } from '@vuelidate/core'
import { mapState } from 'pinia'
import { required, minLength, numeric } from '@vuelidate/validators'
import get from 'lodash/get'
import BorrowerAttributeConstructor from './BorrowerAttributeConstructor.vue'
import BaseBorrowerAttributeMixin from './BaseBorrowerAttributeMixin'
import { useQuestionsStore } from '~/store/questions'

export default {
  props: {
    question: Object,
    alias: String,
    disabled: Boolean,
    repressDisabledStyle: Boolean,
    updateStoreValue: Function,
    borrowerInterest: String,
    validationError: String
  },
  emits: ['invalidity', 'answer'],
  setup: () => ({ v$: useVuelidate() }),
  mixins: [BaseBorrowerAttributeMixin],
  data () {
    return {
      header: 'Owner information',
      value: '',
      errorMessage: 'This field is invalid',
    }
  },
  components: {
    BorrowerAttributeConstructor,
  },
  computed: {
    identifier() {
      return this.question.id || this.question.alias
    },
    ...mapState(useQuestionsStore, ['singleForm']),
    invalid() {
      return !!this.validationError || this.v$.$invalid
    },
    questionText() {
      return this.question.questionText || ''
    },
    description() {
      return this.question.description || ''
    },
    descriptionTooltip() {
      return this.question.descriptionTooltip || ''
    },
    label() {
      return this.question.name || ''
    },
    tooltipText() {
      return this.question.tooltip || this.question.helpText || ''
    },
    helpText() {
      return this.question.tooltip ? this.question.helpText : ''
    },
    errorText() {
      if (this.validationError) {
        return this.validationError
      }
      return get(this.v$, 'value.$error') ? this.errorMessage : ''
    },
    disclaimer() {
      return this.question.disclaimer || ''
    },
    mask() {
      return !this.value && !Number.isFinite(this.value) ? '' : percent(unformattedNumber(this.value))
    },
    options() {
      return {
        preProcess: val => unformattedNumber(val),
        postProcess: val => {
          if (!val) return ''

          this.adjustCursor(this.$refs['percent-input'])
          return percent(unformattedNumber(val))
        },
      }
    }
  },
  created () {
    // Remember question answer from state.
    this.value = this.question.value

    this.$watch('invalid', (invalid) => {
      this.$emit('invalidity', invalid)
    }, {immediate: true})
    switch (this.question.alias) {
      case 'percentOwnership':
      case 'revolvingAvailablePercent':
        this.errorMessage = 'Please enter a number between 0 and 100'
        break
      default:
        this.errorMessage = 'Please enter a number'
    }
  },
  validations: function () {
    if (this.question.alias === 'percentOwnership'
      || this.question.alias === 'revolvingAvailablePercent') {
      return {
        value: { required: required, minLength: minLength(1), pattern: (val) => this.customValidation(val) }
      }
    }
    return {
      value: { required: required, minLength: minLength(1), numeric: numeric }
    }
  },
  methods: {
    customValidation(value) {
      switch (this.question.alias) {
        case 'percentOwnership':
        case 'revolvingAvailablePercent':
          return 0 <= value && value <= 100
        default:
            return true;
        }
    },
    onBlur(v$) {
      // Trigger validation if user entered and exited input without a value.
      v$.$touch()
    },
    onFocus(e) {
      setTimeout(() => this.adjustCursor(e.target), 0)
    },
    adjustCursor(input) {
      if (!input) {
        return
      }

      const val = unformattedNumber(input.value)

      // Change cursor position before suffix if needed
      if (input.selectionEnd != null && (input.selectionEnd > val.length)) {
        input.setSelectionRange(val.length, val.length)
      }
    },
    setValue (e, submitImmediately = false) {
      // Same as onBlur, but ensures validation happens if user enters a value
      // then deletes it before blurring. Shows error sooner.
      this.v$.value.$touch()
      this.value = unformattedNumber(e.target.value)
      this.$emit('answer', { value: this.value, isInvalid: this.v$.value.$error, submitImmediately })
    },
  }
}
</script>

<style scoped lang="scss">
.text-tooltip-icon {
  top: 1rem;
  right: 1rem;
}
</style>
